import React, { useRef, useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import {
  FaTwitter,
  FaLinkedinIn,
  FaFacebookF,
  FaArrowLeft,
  FaRegCalendarAlt,
  FaRegClock,
  FaRegUser,
} from 'react-icons/fa';
import { BLOG_POST_PAGE } from '../config/content';
import { formatMarketDate, REGION } from '../config/region';
import styles from './BlogPost.module.css';
import SEO from '../components/SEO';

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' as const } },
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

function AnimatedSection({ children, className }: { children: React.ReactNode; className?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
      variants={staggerContainer}
      className={className}
    >
      {children}
    </motion.div>
  );
}

interface BlogMeta {
  title: string;
  slug: string;
  excerpt: string;
  category: string;
  date: string;
  readTime: string;
  author: string;
  image: string;
}

/** Simple markdown to HTML converter for blog content */
function markdownToHtml(md: string): string {
  let html = md
    // headings
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    .replace(/^# (.+)$/gm, '<h1>$1</h1>')
    // bold & italic
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    // unordered lists
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    // ordered lists
    .replace(/^\d+\. (.+)$/gm, '<li>$1</li>');

  // wrap consecutive <li> in <ul>
  html = html.replace(/((?:<li>.*<\/li>\n?)+)/g, '<ul>$1</ul>');

  // paragraphs: wrap non-tag lines
  html = html
    .split('\n\n')
    .map((block) => {
      const trimmed = block.trim();
      if (!trimmed) return '';
      if (trimmed.startsWith('<')) return trimmed;
      return `<p>${trimmed.replace(/\n/g, ' ')}</p>`;
    })
    .join('\n');

  return html;
}

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [meta, setMeta] = useState<BlogMeta | null>(null);
  const [content, setContent] = useState('');
  const [relatedPosts, setRelatedPosts] = useState<BlogMeta[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;

    // Fetch blog meta + content + manifest for related posts
    Promise.all([
      fetch(`/data/blogs/${slug}/meta.json`).then((r) => r.json()),
      fetch(`/data/blogs/${slug}/content.md`).then((r) => r.text()),
      fetch('/data/blogs/manifest.json').then((r) => r.json()),
    ])
      .then(([metaData, contentMd, allBlogs]: [BlogMeta, string, BlogMeta[]]) => {
        setMeta(metaData);
        setContent(markdownToHtml(contentMd));
        // Pick 3 related posts (same category, excluding current)
        const related = allBlogs
          .filter((b) => b.slug !== slug)
          .filter((b) => b.category === metaData.category)
          .slice(0, 3);
        // If not enough from same category, fill from others
        if (related.length < 3) {
          const more = allBlogs
            .filter((b) => b.slug !== slug && !related.find((r) => r.slug === b.slug))
            .slice(0, 3 - related.length);
          related.push(...more);
        }
        setRelatedPosts(related);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [slug]);

  const formatDate = (dateStr: string) => formatMarketDate(new Date(dateStr));

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  if (loading) {
    return (
      <div className={styles.page}>
        <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(245,242,235,0.4)' }}>
          {BLOG_POST_PAGE.loading}
        </div>
      </div>
    );
  }

  if (!meta) {
    return (
      <div className={styles.page}>
        <div style={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
          <h2 style={{ color: '#f5f2eb' }}>{BLOG_POST_PAGE.notFound}</h2>
          <Link to={BLOG_POST_PAGE.backLink} style={{ color: '#c9a84c', fontWeight: 600 }}>{BLOG_POST_PAGE.backText}</Link>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <SEO
        title={meta.title}
        description={meta.excerpt}
        path={`/blogs/${meta.slug}`}
        image={`${REGION.site.baseUrl}/data/blogs/${meta.slug}/${meta.image}`}
        type="article"
        article={{
          publishedTime: meta.date,
          author: meta.author,
          section: meta.category,
        }}
      />
      {/* Ambient glows */}
      <div className={`${styles.ambient} ${styles.ambient1}`} />
      <div className={`${styles.ambient} ${styles.ambient2}`} />

      {/* Content */}
      <section className={styles.contentSection}>
        <div className={styles.container}>
          {/* Featured Image Banner — inline with content */}
          <div className={styles.featuredImage}>
            <img src={`/data/blogs/${meta.slug}/${meta.image}`} alt={meta.title} />
            <div className={styles.featuredOverlay} />
          </div>

          <div className={styles.layout}>
            {/* Main Article */}
            <AnimatedSection className={styles.article}>
              <motion.div variants={fadeInUp}>
                <Link to={BLOG_POST_PAGE.backLink} className={styles.backLink}>
                  <FaArrowLeft /> {BLOG_POST_PAGE.backText}
                </Link>
              </motion.div>

              <motion.span className={styles.category} variants={fadeInUp}>
                {meta.category}
              </motion.span>

              <motion.h1 className={styles.title} variants={fadeInUp}>
                {meta.title}
              </motion.h1>

              <motion.div className={styles.meta} variants={fadeInUp}>
                <span className={styles.metaItem}>
                  <FaRegCalendarAlt /> {formatDate(meta.date)}
                </span>
                <span className={styles.metaItem}>
                  <FaRegUser /> {meta.author}
                </span>
                <span className={styles.metaItem}>
                  <FaRegClock /> {meta.readTime}
                </span>
              </motion.div>

              <motion.div className={styles.shareRow} variants={fadeInUp}>
                <span className={styles.shareLabel}>{BLOG_POST_PAGE.shareLabel}</span>
                <a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" className={styles.shareBtn} aria-label="Share on Twitter">
                  <FaTwitter />
                </a>
                <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" className={styles.shareBtn} aria-label="Share on LinkedIn">
                  <FaLinkedinIn />
                </a>
                <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" className={styles.shareBtn} aria-label="Share on Facebook">
                  <FaFacebookF />
                </a>
              </motion.div>

              <motion.div
                className={styles.body}
                variants={fadeInUp}
                dangerouslySetInnerHTML={{ __html: content }}
              />
            </AnimatedSection>

            {/* Sidebar */}
            <aside className={styles.sidebar}>
              <AnimatedSection>
                <motion.div className={styles.sidebarCard} variants={fadeInUp}>
                  <h3 className={styles.sidebarTitle}>{BLOG_POST_PAGE.relatedTitle}</h3>
                  <div className={styles.relatedList}>
                    {relatedPosts.map((rp) => (
                      <Link to={`/blogs/${rp.slug}`} className={styles.relatedLink} key={rp.slug}>
                        <div className={styles.relatedThumb}>
                          <img src={`/data/blogs/${rp.slug}/${rp.image}`} alt={rp.title} />
                        </div>
                        <span className={styles.relatedTitle}>{rp.title}</span>
                      </Link>
                    ))}
                  </div>
                </motion.div>

                <motion.div className={styles.sidebarCard} variants={fadeInUp}>
                  <h3 className={styles.sidebarTitle}>{BLOG_POST_PAGE.shareTitle}</h3>
                  <div className={styles.sidebarShareRow}>
                    <a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" className={styles.sidebarShareBtn} aria-label="Twitter">
                      <FaTwitter />
                    </a>
                    <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" className={styles.sidebarShareBtn} aria-label="LinkedIn">
                      <FaLinkedinIn />
                    </a>
                    <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" className={styles.sidebarShareBtn} aria-label="Facebook">
                      <FaFacebookF />
                    </a>
                  </div>
                </motion.div>
              </AnimatedSection>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPost;
